<template>
  <div class="admin-page">
    <admin-page-header
      eyebrow="Finans"
      title="Para Transfer İstekleri"
      description="Ödeme durumu, fatura alanları ve kullanıcı banka bilgilerini aynı ekrandan takip edin."
    ></admin-page-header>

    <admin-request-state
      :error="loadError"
      :loading="loading"
      @retry="initialize"
    />
    <v-alert
      v-if="successMessage"
      type="success"
      dense
      outlined
      dismissible
    >
      {{ successMessage }}
    </v-alert>
    <div class="payment-filters">
      <v-text-field
        v-model="search"
        label="Kullanıcı, e-posta veya talep no"
        outlined
        dense
        hide-details
        clearable
      />
      <v-select
        v-model="statusFilter"
        :items="statusOptions"
        label="Ödeme durumu"
        outlined
        dense
        hide-details
      />
      <v-text-field
        v-model="dateFrom"
        type="date"
        label="İlk talep tarihi"
        outlined
        dense
        hide-details
      />
      <v-text-field
        v-model="dateTo"
        type="date"
        label="Son talep tarihi"
        outlined
        dense
        hide-details
      />
      <v-btn
        icon
        :loading="loading"
        title="Talepleri yenile"
        aria-label="Talepleri yenile"
        @click="initialize"
      >
        <v-icon>{{ icons.mdiRefresh }}</v-icon>
      </v-btn>
    </div>

    <v-card
      class="admin-table-card"
      outlined
    >
      <v-data-table
        :loading="loading"
        loading-text="Yükleniyor"
        :headers="headers"
        :items="filteredRequests"
        :sort-by="['date']"
        class="admin-data-table elevation-0"
      >
        <template v-slot:top>
          <v-toolbar
            dense
            :elevation="0"
            class="admin-page-toolbar"
          >
            <v-toolbar-title>Ödeme talepleri</v-toolbar-title>
            <v-spacer></v-spacer>

            <v-dialog
              v-model="invoiceDialog"
              max-width="600px"
            >
              <invoice-dialog
                :close="closeInvoice"
                :item="editedItem"
              />
            </v-dialog>

            <tax-document-preview
              v-if="documentPreviewDialog && documentPreviewItem"
              :key="documentPreviewItem.id"
              :document-id="documentPreviewItem.id"
              :headers="requestOptions.headers"
              @close="closeDocumentPreview"
            ></tax-document-preview>

            <v-dialog
              v-model="documentRejectDialog"
              max-width="560px"
              persistent
            >
              <v-card class="admin-dialog-card">
                <v-toolbar
                  dense
                  :elevation="0"
                >
                  <v-toolbar-title>İstisna belgesini geçersiz işaretle</v-toolbar-title>
                </v-toolbar>

                <v-card-text class="pt-4">
                  <v-alert
                    dense
                    outlined
                    type="warning"
                  >
                    Bu karar belgeyi geçersiz sayar ve belgeye bağlı tüm bekleyen para çekme taleplerini aynı işlemde reddedildi durumuna getirir.
                  </v-alert>
                  <v-textarea
                    v-model="documentRejectReason"
                    :counter="500"
                    maxlength="500"
                    label="Geçersiz Sayma Sebebi"
                    outlined
                    rows="3"
                    auto-grow
                  ></v-textarea>
                  <v-alert
                    v-if="documentReviewError"
                    dense
                    outlined
                    type="error"
                  >
                    {{ documentReviewError }}
                  </v-alert>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn
                    text
                    :disabled="documentReviewLoading"
                    @click="closeDocumentRejectDialog"
                  >
                    Vazgeç
                  </v-btn>
                  <v-btn
                    color="error"
                    :loading="documentReviewLoading"
                    :disabled="documentReviewLoading"
                    @click="submitDocumentReview('REJECT')"
                  >
                    Geçersiz Say
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <v-dialog
              v-model="dialog"
              max-width="1000px"
              persistent
            >
              <v-card class="admin-dialog-card">
                <v-toolbar
                  dense
                  :elevation="0"
                >
                  <v-toolbar-title>{{ formTitle }} #{{ editedItem.id }}</v-toolbar-title>
                </v-toolbar>

                <v-card-text class="payment-review">
                  <section class="payment-context">
                    <h3>{{ recipientName(editedItem) }}</h3>
                    <router-link
                      v-if="recipientId && canViewUsers"
                      :to="{name: 'userList', query: {userId: String(recipientId)}}"
                    >
                      Kullanıcı kaydı
                    </router-link>
                    <dl class="payment-facts">
                      <dt>Talep tarihi</dt><dd>{{ formatTransferDate(editedItem.date) }}</dd>
                      <dt>Mevcut durum</dt><dd>{{ statusLabel(editedItem.status) }}</dd>
                      <dt>Vergi hariç tutar</dt><dd>{{ formatAmount(editedItem.requestAmount) }}</dd>
                      <dt>KDV</dt><dd>{{ formatAmount(getTaxValue(editedItem, 'Kdv')) }}</dd>
                      <dt>Stopaj</dt><dd>{{ formatAmount(getTaxValue(editedItem, 'Stopaj')) }}</dd>
                      <dt>Net transfer</dt><dd class="font-weight-bold">
                        {{ formatAmount(editedItem.netAmount) }}
                      </dd>
                      <dt>Banka</dt><dd>{{ (editedItem.walletBank || {}).bank && editedItem.walletBank.bank.name || '-' }}</dd>
                      <dt>Hesap sahibi</dt><dd>{{ (editedItem.walletBank || {}).accountOwner || '-' }}</dd>
                      <dt>IBAN</dt><dd>{{ (editedItem.walletBank || {}).iban || '-' }}</dd>
                      <dt>Belge türü</dt><dd>{{ getInvoiceType(editedItem.invoiceType) }}</dd>
                    </dl>
                    <v-btn
                      outlined
                      small
                      @click="openInvoice(editedItem)"
                    >
                      <v-icon
                        left
                        small
                      >
                        {{ icons.mdiEye }}
                      </v-icon>Fatura bilgileri
                    </v-btn>
                    <details class="mt-4">
                      <summary>İletişim ve vergi bilgileri</summary>
                      <dl class="payment-facts">
                        <dt>E-posta</dt><dd>{{ recipient.email || '-' }}</dd>
                        <dt>Telefon</dt><dd>{{ recipient.mobile || '-' }}</dd>
                        <dt>Vergi dairesi</dt><dd>{{ editedItem.taxOffice || '-' }}</dd>
                        <dt>Vergi numarası</dt><dd>{{ editedItem.taxNumber || '-' }}</dd>
                        <dt>Adres</dt><dd>{{ [editedItem.address, editedItem.town, editedItem.city].filter(Boolean).join(', ') || '-' }}</dd>
                      </dl>
                    </details>
                  </section>
                  <v-container class="pa-0">
                    <v-row>
                      <v-col cols="12">
                        <label class="text-caption font-weight-bold">Ödeme Durumu</label>
                        <v-radio-group
                          v-model="paymentStatus"
                          class="mt-2"
                          row
                          :disabled="saving"
                        >
                          <v-radio
                            label="Bekleyen"
                            value="PENDING"
                            @click="changeAccept"
                          ></v-radio>
                          <v-radio
                            label="Reddedildi"
                            value="FAILED"
                            :disabled="genericFailedDecisionBlocked"
                            @click="changeAccept"
                          ></v-radio>
                          <v-radio
                            label="Tamamlandı"
                            value="SUCCESS"
                            @click="changeAccept"
                          ></v-radio>
                        </v-radio-group>
                      </v-col>

                      <v-col
                        v-if="paymentStatus === 'FAILED'"
                        cols="12"
                      >
                        <v-text-field
                          v-model="editedItem.failDescription"
                          label="Reddetme Sebebi"
                          outlined
                          dense
                          :disabled="saving"
                          maxlength="500"
                        ></v-text-field>
                      </v-col>

                      <v-col
                        v-if="editedItem.invoiceTypeCompany === 'COMPANY'"
                        cols="12"
                      >
                        <company-invoice-documents
                          :key="editedItem.id"
                          :withdrawal-id="editedItem.id"
                          :headers="requestOptions.headers"
                        ></company-invoice-documents>
                      </v-col>
                      <v-col
                        v-if="hasDigitalBusinessDocumentSnapshot"
                        cols="12"
                      >
                        <v-card
                          class="digital-business-documents pa-4"
                          outlined
                        >
                          <div class="d-flex align-start justify-space-between flex-wrap mb-2">
                            <div>
                              <div class="text-subtitle-1 font-weight-bold">
                                20/B istisna belgesi
                              </div>
                              <div class="text-caption text--secondary">
                                Kullanıcının fatura bilgileri kapsamında saklanan talep anı belge sürümünü inceleyin. Geçerli kararı sonraki Dijital İşim taleplerinde de kullanılır; transferi otomatik tamamlamaz.
                              </div>
                            </div>
                            <v-btn
                              v-if="documentLoadError"
                              color="primary"
                              outlined
                              small
                              :loading="documentLoading"
                              @click="loadWithdrawalDocuments(editedItem)"
                            >
                              Tekrar dene
                            </v-btn>
                          </div>

                          <v-progress-linear
                            v-if="documentLoading"
                            color="primary"
                            indeterminate
                            class="my-3"
                          ></v-progress-linear>
                          <v-alert
                            v-else-if="documentLoadError"
                            dense
                            outlined
                            type="error"
                            class="mt-3"
                          >
                            {{ documentLoadError }}
                          </v-alert>
                          <v-alert
                            v-if="documentSuccessMessage"
                            dense
                            outlined
                            type="success"
                            class="mt-3"
                          >
                            {{ documentSuccessMessage }}
                          </v-alert>
                          <v-alert
                            v-if="hasPendingDigitalBusinessDocuments"
                            dense
                            outlined
                            type="warning"
                            class="mt-3"
                          >
                            İnceleme bekleyen belge varken genel “Reddedildi” kararı verilemez. Belgeyi ve bağlı bekleyen para çekme taleplerini birlikte reddetmek için “Geçersiz” aksiyonunu kullanın.
                          </v-alert>

                          <div
                            v-for="document in withdrawalDocuments"
                            :key="document.id"
                            class="digital-business-documents__item mt-3 pa-3"
                          >
                            <div class="digital-business-documents__item-header">
                              <div>
                                <div class="font-weight-bold">
                                  {{ documentTypeLabel(document.type) }}
                                  <span class="text-caption text--secondary">v{{ document.version || '-' }}</span>
                                </div>
                                <div class="text-caption text--secondary mt-1">
                                  {{ document.originalFileName || 'Dosya adı paylaşılmadı' }} · {{ formatDocumentBytes(document.sizeBytes) }}
                                </div>
                              </div>
                              <v-chip
                                small
                                outlined
                                :color="documentStatusColor(document.status)"
                              >
                                {{ documentStatusLabel(document.status) }}
                              </v-chip>
                            </div>

                            <div class="text-caption mt-2">
                              Talebe eklenen kullanıcı seviyesindeki 20/B vergi istisnası belgesi
                            </div>
                            <div
                              v-if="document.rejectReason"
                              class="text-caption error--text mt-2"
                            >
                              Red gerekçesi: {{ document.rejectReason }}
                            </div>

                            <div class="digital-business-documents__actions mt-3">
                              <v-btn
                                v-if="canPreviewDocument(document)"
                                color="primary"
                                outlined
                                small
                                :disabled="isDocumentContentBusy(document.id)"
                                @click="openDocumentPreview(document)"
                              >
                                Görüntüle
                              </v-btn>
                              <v-spacer></v-spacer>
                              <v-btn
                                v-if="canReviewDocument(document)"
                                color="success"
                                outlined
                                small
                                :loading="documentReviewAction === `approve-${document.id}`"
                                :disabled="documentReviewLoading"
                                @click="approveDocument(document)"
                              >
                                Geçerli
                              </v-btn>
                              <v-btn
                                v-if="canReviewDocument(document)"
                                color="error"
                                outlined
                                small
                                :disabled="documentReviewLoading"
                                @click="openDocumentRejectDialog(document)"
                              >
                                Geçersiz
                              </v-btn>
                            </div>
                          </div>

                          <v-alert
                            v-if="!documentLoading && !documentLoadError && withdrawalDocuments.length === 0"
                            dense
                            outlined
                            type="warning"
                            class="mt-3 mb-0"
                          >
                            Bu para çekme talebine eklenen belge sürümleri bulunamadı. Belge durumu doğrulanmadan transfer tamamlanamaz.
                          </v-alert>
                        </v-card>
                      </v-col>

                      <v-col cols="12">
                        <label class="text-caption font-weight-bold">Fatura</label>
                        <v-radio-group
                          v-model="editedItem.panelInvoice"
                          row
                          class="mt-2"
                          :disabled="saving"
                        >
                          <v-radio
                            label="Kesilmedi"
                            :value="false"
                          ></v-radio>
                          <v-radio
                            label="Kesildi"
                            :value="true"
                          ></v-radio>
                        </v-radio-group>
                      </v-col>

                      <v-col
                        v-if="hasChanges"
                        cols="12"
                      >
                        <v-alert
                          type="warning"
                          dense
                          outlined
                        >
                          {{ statusLabel(editedItem.status) }} → {{ statusLabel(paymentStatus) }} · {{ formatAmount(editedItem.netAmount) }}
                        </v-alert>
                        <v-checkbox
                          v-model="decisionConfirmed"
                          :disabled="saving"
                          label="Alıcıyı, tutarı ve durum değişikliğini kontrol ettim."
                          hide-details
                        />
                      </v-col>
                      <v-col
                        v-if="formError"
                        cols="12"
                      >
                        <v-alert
                          dense
                          outlined
                          type="error"
                        >
                          {{ formError }}
                        </v-alert>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn
                    color="primary"
                    text
                    :disabled="saving"
                    @click="close"
                  >
                    Vazgeç
                  </v-btn>
                  <v-btn
                    color="primary"
                    :loading="saving"
                    :disabled="saving || !hasChanges || !decisionConfirmed"
                    @click="save(editedItem)"
                  >
                    Değişiklikleri kaydet
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
        </template>

        <template v-slot:item.action="{ item }">
          <v-btn
            icon
            title="Talebi incele"
            aria-label="Talebi incele"
            @click="editItem(item)"
          >
            <v-icon>{{ icons.mdiPencil }}</v-icon>
          </v-btn>
        </template>

        <template v-slot:item.recipient="{ item }">
          <strong>{{ recipientName(item) }}</strong><div class="text-caption">
            #{{ item.id }}
          </div>
        </template>
        <template v-slot:item.waiting="{ item }">
          {{ waitingTime(item) }}
        </template>

        <template v-slot:item.walletBank="{ item }">
          <td :style="{ width: '100px' }">
            {{ item.walletBank.bank.name }}
          </td>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip
            small
            outlined
            :color="item.status === 'PENDING' ? 'warning' : item.status === 'SUCCESS' ? 'success' : 'error'"
          >
            {{ item.status === 'PENDING' ? 'Bekleyen' : item.status === 'SUCCESS' ? 'Tamamlandı' : 'Reddedildi' }}
          </v-chip>
        </template>

        <template v-slot:item.panelInvoice="{ item }">
          <v-chip
            small
            outlined
            :color="item.panelInvoice ? 'success' : 'error'"
          >
            {{ item.panelInvoice ? 'Kesildi' : 'Kesilmedi' }}
          </v-chip>
        </template>

        <template v-slot:item.date="{ item }">
          {{ formatTransferDate(item.date) }}
        </template>

        <template v-slot:item.requestAmount="{ item }">
          {{ formatAmount(item.requestAmount) }}
        </template>

        <template #[`item.taxValues.Kdv`]="{ item }">
          {{ formatAmount(getTaxValue(item, 'Kdv')) }}
        </template>

        <template #[`item.taxValues.Stopaj`]="{ item }">
          {{ formatAmount(getTaxValue(item, 'Stopaj')) }}
        </template>

        <template v-slot:item.netAmount="{ item }">
          {{ formatAmount(item.netAmount) }}
        </template>

        <template #[`item.walletBank.user`]="{ item }">
          <td :style="{ width: '100px' }">
            {{ item.walletBank.user.name }} {{ item.walletBank.user.surname }}
          </td>
        </template>

        <template #[`item.user.emailAddress`]="{ item }">
          <td :style="{ width: '100px' }">
            {{ item.walletBank.user.email }}
          </td>
        </template>

        <template v-slot:item.invoiceTypeCompany="{ item }">
          <td>{{ item.invoiceTypeCompany !== null ? getPaymentType(item.invoiceTypeCompany) : '-' }}</td>
        </template>

        <template v-slot:item.taxOffice="{ item }">
          <td>{{ item.taxOffice !== null ? item.taxOffice : '-' }}</td>
        </template>

        <template v-slot:item.invoiceType="{ item }">
          <td>{{ getInvoiceType(item.invoiceType) }}</td>
        </template>

        <template #[`item.invoiceInfo.number`]="{ item }">
          <td>{{ item.taxNumber !== null ? item.taxNumber : '-' }}</td>
        </template>

        <template #[`item.invoiceInfo.taxNumber`]="{ item }">
          <td>{{ item.taxNumber !== null ? item.taxNumber : '-' }}</td>
        </template>

        <template #[`item.addressInfo`]="{ item }">
          <td :style="{ width: '30px' }">
            {{ item.address !== null ? item.address : '-' }}
            {{ item.town !== null ? item.town : '-' }}
            {{ item.city !== null ? item.city : '-' }}
          </td>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import axios from 'axios'
import {
  mdiClose, mdiDelete, mdiPencil, mdiEye, mdiRefresh,
} from '@mdi/js'
import { getAccessToken, getPanelSession, hasAdminAccess } from '@/utils/panelSession'
import { resolveApiErrorMessage } from '@/utils/apiErrors'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'
import AdminRequestState from '@/components/admin/AdminRequestState.vue'
import TaxDocumentPreview from '@/components/admin/TaxDocumentPreview.vue'
import CompanyInvoiceDocuments from '@/components/admin/CompanyInvoiceDocuments.vue'
import InvoiceDialog from './InvoiceDialog.vue'

const user = getAccessToken()
const DOCUMENT_TYPES = Object.freeze({
  TAX_EXEMPTION_20B: '20/B vergi istisnası belgesi',
})
const DOCUMENT_STATUSES = Object.freeze({
  PENDING_REVIEW: 'İnceleme bekliyor',
  APPROVED: 'Geçerli',
  REJECTED: 'Geçersiz',
})
const PREVIEWABLE_IMAGE_TYPES = Object.freeze(['application/pdf', 'image/png', 'image/jpeg', 'image/webp'])

export default {
  components: {
    AdminPageHeader,
    AdminRequestState,
    InvoiceDialog,
    TaxDocumentPreview,
    CompanyInvoiceDocuments,
  },
  setup() {
    return {
      PAYMENT_TYPE: {
        PERSONAL: 'Şahıs Şirketi',
        COMPANY: ' LTD. /A.Ş.',
        NONE: 'YOK',
      },
      INVIOCE_TYPE: {
        SELF_EMPLOYMENT: 'Serbest Meslek Makbuzu',
        EXPENSE_COMPASS: 'Gider Pusulası',
        CUSTOM_BANK: 'Özel Banka',
        INVOICE: 'Fatura',
      },
    }
  },
  data: () => ({
    dialog: false,
    loading: false,
    saving: false,
    formError: '',
    loadError: '',
    successMessage: '',
    search: '',
    statusFilter: 'PENDING',
    dateFrom: '',
    dateTo: '',
    decisionConfirmed: false,
    originalInvoice: false,
    originalDescription: '',
    statusOptions: [{ text: 'Bekleyen', value: 'PENDING' }, { text: 'Tamamlandı', value: 'SUCCESS' }, { text: 'Reddedildi', value: 'FAILED' }, { text: 'Tümü', value: 'ALL' }],
    invoiceItem: {},
    headers: [
      { text: 'Alıcı / Talep', value: 'recipient', sortable: false },
      { text: 'Durum', value: 'status' },
      { text: 'Net tutar', value: 'netAmount' },
      { text: 'Banka', value: 'walletBank.bank.name' },
      { text: 'Fatura', value: 'panelInvoice' },
      { text: 'Talep tarihi', value: 'date' },
      { text: 'Bekleme', value: 'waiting', sortable: false },
      {
        text: 'İncele', value: 'action', sortable: false, width: 64,
      },
    ],
    desserts: [],
    rules: {
      required: value => !!value || 'Zorunlu.',
    },
    paymentStatus: '',
    icons: {
      mdiClose,
      mdiPencil,
      mdiDelete,
      mdiEye,
      mdiRefresh,
    },
    editedIndex: -1,
    requestOptions: {
      headers: { Authorization: `Bearer ${user}` },
    },
    withdrawalDocuments: [],
    documentLoading: false,
    documentLoadError: '',
    documentSuccessMessage: '',
    documentRequestSerial: 0,
    documentContentAction: '',
    documentPreviewDialog: false,
    documentPreviewItem: null,
    documentPreviewUrl: '',
    documentPreviewLoading: false,
    documentPreviewError: '',
    documentPreviewRequestSerial: 0,
    documentReviewItem: null,
    documentRejectDialog: false,
    documentRejectReason: '',
    documentReviewLoading: false,
    documentReviewAction: '',
    documentReviewError: '',
    invoiceDialog: false,
    editedItem: {
      id: '',
      name: '',
      logo: '',
      image: '',
      panelInvoice: false,
      categories: [{ id: '' }],
    },
  }),
  computed: {
    canViewUsers() { return hasAdminAccess(getPanelSession()) },
    recipient() { return this.editedItem.walletBank?.user || this.editedItem.user || {} },
    recipientId() { return this.recipient.id },
    hasChanges() {
      return this.paymentStatus !== this.editedItem.status || this.normalizeBoolean(this.editedItem.panelInvoice) !== this.originalInvoice
        || String(this.editedItem.failDescription || '').trim() !== this.originalDescription
    },
    filteredRequests() {
      const search = String(this.search || '').trim().toLocaleLowerCase('tr-TR')

      return this.desserts.filter(item => {
        if (this.statusFilter !== 'ALL' && item.status !== this.statusFilter) return false
        const date = this.dateKey(item.date)
        if (this.dateFrom && (!date || date < this.dateFrom)) return false
        if (this.dateTo && (!date || date > this.dateTo)) return false

        return !search || [item.id, this.recipientName(item), item.walletBank?.user?.email].join(' ').toLocaleLowerCase('tr-TR').includes(search)
      })
    },
    formTitle() {
      return this.editedIndex === -1 ? '-' : 'Para Transfer İstekleri'
    },
    documentApiBase() {
      return `${process.env.VUE_APP_BASE_API_URL}admin/digital-business-documents`
    },
    hasDigitalBusinessDocumentSnapshot() {
      return this.snapshotDocumentIds(this.editedItem).length > 0
        || this.embeddedWithdrawalDocuments(this.editedItem).length > 0
    },
    hasPendingDigitalBusinessDocuments() {
      return this.withdrawalDocuments.some(document => document.status === 'PENDING_REVIEW')
    },
    genericFailedDecisionBlocked() {
      return this.hasDigitalBusinessDocumentSnapshot
        && (this.documentLoading
          || Boolean(this.documentLoadError)
          || !this.hasCompleteDigitalBusinessDocumentSnapshot()
          || this.hasPendingDigitalBusinessDocuments)
    },
    documentPreviewTitle() {
      if (!this.documentPreviewItem) return 'Belge önizleme'

      return `${this.documentTypeLabel(this.documentPreviewItem.type)} v${this.documentPreviewItem.version || '-'}`
    },
  },
  watch: {
    paymentStatus() { this.decisionConfirmed = false },
    'editedItem.panelInvoice': function invoiceChanged() { this.decisionConfirmed = false },
    'editedItem.failDescription': function descriptionChanged() { this.decisionConfirmed = false },
    dialog(val) {
      if (val) this.paymentStatus = this.editedItem.status
    },
  },
  mounted() {
    this.paymentStatus = this.editedItem.status
  },
  created() {
    this.initialize()
  },
  beforeDestroy() {
    this.documentRequestSerial += 1
    this.documentPreviewRequestSerial += 1
    this.revokeDocumentPreviewUrl()
  },
  methods: {
    statusLabel(value) { return this.statusOptions.find(item => item.value === value)?.text || value || '-' },
    recipientName(item) {
      const person = item.walletBank?.user || item.user || {}

      return [person.name, person.surname].filter(Boolean).join(' ') || 'Kullanıcı bilgisi yok'
    },
    dateKey(value) {
      if (!value) return ''
      const numeric = /^\d{10,13}$/.test(String(value)) ? Number(value) * (String(value).length <= 10 ? 1000 : 1) : value
      const date = new Date(numeric)

      return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' })
    },
    waitingTime(item) {
      if (item.status !== 'PENDING') return '-'
      const key = this.dateKey(item.date)
      if (!key) return '-'

      return `${Math.max(0, Math.floor((Date.now() - new Date(`${key}T00:00:00+03:00`).getTime()) / 86400000))} gün`
    },
    getPaymentType(type = '') {
      return this.PAYMENT_TYPE[type] || '-'
    },
    getInvoiceType(type = '') {
      return this.INVIOCE_TYPE[type] || '-'
    },
    normalizeBoolean(value) {
      return value === true || value === 'true' || value === 1 || value === '1'
    },
    normalizeDocumentId(value) {
      const documentId = Number(value)

      return Number.isInteger(documentId) && documentId > 0 ? documentId : null
    },
    snapshotDocumentIds(item = {}) {
      return [
        item.taxExemption20bDocumentVersionId,
        item.tax_exemption_20b_document_version_id,
      ]
        .map(this.normalizeDocumentId)
        .filter((value, index, values) => value && values.indexOf(value) === index)
    },
    embeddedWithdrawalDocuments(item = {}) {
      const value = item.digitalBusinessDocuments || item.digitalBusinessDocumentContext
      if (Array.isArray(value)) return value
      if (Array.isArray(value?.documents)) return value.documents

      return []
    },
    normalizeWithdrawalDocument(document) {
      if (!document || !this.normalizeDocumentId(document.id)) return null

      return {
        ...document,
        id: this.normalizeDocumentId(document.id),
        pendingWithdrawalRequestIdsProvided: Array.isArray(
          document.pendingWithdrawalRequestIds,
        ),
        withdrawalRequestIds: Array.isArray(document.withdrawalRequestIds)
          ? document.withdrawalRequestIds.map(this.normalizeDocumentId).filter(Boolean)
          : [],
        pendingWithdrawalRequestIds: Array.isArray(document.pendingWithdrawalRequestIds)
          ? document.pendingWithdrawalRequestIds.map(this.normalizeDocumentId).filter(Boolean)
          : [],
      }
    },
    sortWithdrawalDocuments(documents) {
      return [...documents].sort((left, right) => Number(right.version || 0) - Number(left.version || 0))
    },
    hasCompleteDigitalBusinessDocumentSnapshot() {
      const expectedIds = this.snapshotDocumentIds(this.editedItem)
      const validStatuses = ['PENDING_REVIEW', 'APPROVED', 'REJECTED']
      const exactDocuments = expectedIds.length > 0
        ? this.withdrawalDocuments.filter(document => expectedIds.includes(document.id))
        : this.withdrawalDocuments

      if (expectedIds.length > 0 && expectedIds.length !== 1) return false
      if (exactDocuments.length !== 1) return false

      return exactDocuments[0].type === 'TAX_EXEMPTION_20B'
        && exactDocuments.every(document => validStatuses.includes(document.status))
        && (expectedIds.length === 0
          || expectedIds.every(id => exactDocuments.some(document => document.id === id)))
    },
    async loadWithdrawalDocuments(item = this.editedItem) {
      const withdrawalRequestId = this.normalizeDocumentId(item?.id)
      const expectedIds = this.snapshotDocumentIds(item)
      const embeddedDocuments = this.embeddedWithdrawalDocuments(item)
        .map(this.normalizeWithdrawalDocument)
        .filter(Boolean)
      const hasSnapshot = expectedIds.length > 0 || embeddedDocuments.length > 0
      const requestId = this.documentRequestSerial + 1
      this.documentRequestSerial = requestId
      this.withdrawalDocuments = []
      this.documentLoadError = ''
      this.documentSuccessMessage = ''

      if (!hasSnapshot || !withdrawalRequestId) {
        this.documentLoading = false

        return
      }

      if (embeddedDocuments.length > 0
        && (expectedIds.length === 0
          || expectedIds.every(id => embeddedDocuments.some(document => document.id === id)))) {
        this.withdrawalDocuments = this.sortWithdrawalDocuments(
          expectedIds.length > 0
            ? embeddedDocuments.filter(document => expectedIds.includes(document.id))
            : embeddedDocuments,
        )
        this.documentLoading = false

        return
      }

      this.documentLoading = true
      try {
        const response = await axios.get(this.documentApiBase, {
          ...this.requestOptions,
          params: {
            withdrawalRequestId,
            page: 0,
            size: 10,
          },
        })
        if (requestId !== this.documentRequestSerial) return

        const payload = response.data?.data || response.data || {}
        let content = []
        if (Array.isArray(payload)) {
          content = payload
        } else if (Array.isArray(payload.content)) {
          content = payload.content
        }
        const normalized = content
          .map(document => this.normalizeWithdrawalDocument(document))
          .filter(Boolean)
        const associated = normalized.filter(document => (
          expectedIds.includes(document.id)
          || document.withdrawalRequestIds.includes(withdrawalRequestId)
          || document.pendingWithdrawalRequestIds.includes(withdrawalRequestId)
        ))
        const exactDocuments = expectedIds.length > 0
          ? associated.filter(document => expectedIds.includes(document.id))
          : associated
        const missingIds = expectedIds.filter(id => !exactDocuments.some(document => document.id === id))

        if (missingIds.length > 0 || exactDocuments.length === 0) {
          this.withdrawalDocuments = this.sortWithdrawalDocuments(exactDocuments)
          this.documentLoadError = 'Talebe eklenen belge sürümlerinin tamamı doğrulanamadı. Transfer kararı vermeden önce tekrar deneyin.'

          return
        }

        this.withdrawalDocuments = this.sortWithdrawalDocuments(exactDocuments)
      } catch (error) {
        if (requestId !== this.documentRequestSerial) return

        this.withdrawalDocuments = []
        this.documentLoadError = this.resolveDocumentError(
          error,
          'Talebe eklenen 20/B istisna belgesi yüklenemedi.',
        )
      } finally {
        if (requestId === this.documentRequestSerial) this.documentLoading = false
      }
    },
    getTaxValue(item, key) {
      if (!item || !item.taxValues) return null

      return item.taxValues[key]
    },
    formatAmount(value) {
      if (value === null || value === undefined || value === '') return '-'

      const amount = Number(value)
      if (!Number.isFinite(amount)) return '-'

      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount)
    },
    formatTransferDate(value) {
      if (value === null || value === undefined || value === '') return '-'

      const normalizedValue = String(value).trim()
      const numericTimestamp = /^\d{10,13}$/.test(normalizedValue)
        ? Number(normalizedValue) * (normalizedValue.length <= 10 ? 1000 : 1)
        : value
      const date = new Date(numericTimestamp)
      if (Number.isNaN(date.getTime())) return '-'

      return date.toLocaleString('tr-TR', {
        timeZone: 'Europe/Istanbul',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      })
    },
    documentTypeLabel(type) {
      return DOCUMENT_TYPES[type] || type || '-'
    },
    documentStatusLabel(status) {
      return DOCUMENT_STATUSES[status] || status || '-'
    },
    documentStatusColor(status) {
      const colors = {
        PENDING_REVIEW: 'warning',
        APPROVED: 'success',
        REJECTED: 'error',
      }

      return colors[status] || 'grey'
    },
    formatDocumentBytes(value) {
      const bytes = Number(value)
      if (!Number.isFinite(bytes) || bytes < 0) return '-'
      if (bytes === 0) return '0 B'

      const units = ['B', 'KB', 'MB', 'GB']
      const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
      const amount = bytes / (1024 ** unitIndex)

      return `${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 1 }).format(amount)} ${units[unitIndex]}`
    },
    canReviewDocument(document) {
      return Boolean(document && document.status === 'PENDING_REVIEW')
    },
    canPreviewDocument(document) {
      return Boolean(
        document?.contentAvailable
        && PREVIEWABLE_IMAGE_TYPES.includes(String(document.contentType || '').toLowerCase()),
      )
    },
    isDocumentContentBusy(documentId) {
      return Boolean(documentId && this.documentContentAction)
    },
    async requestDocumentContent(document) {
      return axios.get(`${this.documentApiBase}/${document.id}/content`, {
        ...this.requestOptions,
        responseType: 'blob',
      })
    },
    openDocumentPreview(document) {
      if (!this.canPreviewDocument(document) || this.isDocumentContentBusy(document.id)) return

      this.revokeDocumentPreviewUrl()
      this.documentPreviewItem = document
      this.documentPreviewDialog = true
    },
    handleDocumentPreviewDialogInput(value) {
      if (!value) this.closeDocumentPreview()
    },
    closeDocumentPreview() {
      this.documentPreviewRequestSerial += 1
      this.documentPreviewDialog = false
      this.documentPreviewLoading = false
      this.documentPreviewError = ''
      this.documentPreviewItem = null
      this.documentContentAction = ''
      this.revokeDocumentPreviewUrl()
    },
    revokeDocumentPreviewUrl() {
      if (this.documentPreviewUrl) URL.revokeObjectURL(this.documentPreviewUrl)
      this.documentPreviewUrl = ''
    },
    safeDocumentFileName(document) {
      const fallback = `digital-business-document-${document.id}`
      const withoutControlCharacters = Array.from(String(document.originalFileName || fallback))
        .map(character => (character.charCodeAt(0) < 32 ? '_' : character))
        .join('')
      const value = withoutControlCharacters
        .replace(/[<>:"/\\|?*]/g, '_')
        .trim()

      return value || fallback
    },
    async downloadDocument(document) {
      if (!document?.contentAvailable || this.isDocumentContentBusy(document.id)) return

      this.documentContentAction = `download-${document.id}`
      this.documentLoadError = ''
      try {
        const response = await this.requestDocumentContent(document)
        const objectUrl = URL.createObjectURL(response.data)
        const link = window.document.createElement('a')
        link.href = objectUrl
        link.download = this.safeDocumentFileName(document)
        link.rel = 'noopener'
        window.document.body.appendChild(link)
        link.click()
        window.document.body.removeChild(link)
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
      } catch (error) {
        this.documentLoadError = this.resolveDocumentError(error, 'Belge indirilemedi.')
      } finally {
        this.documentContentAction = ''
      }
    },
    approveDocument(document) {
      if (!this.canReviewDocument(document)) return

      this.documentReviewItem = document
      this.documentReviewError = ''
      this.documentRejectReason = ''
      this.submitDocumentReview('APPROVE')
    },
    openDocumentRejectDialog(document) {
      if (!this.canReviewDocument(document)) return

      this.documentReviewItem = document
      this.documentRejectReason = ''
      this.documentReviewError = ''
      this.documentRejectDialog = true
    },
    closeDocumentRejectDialog() {
      if (this.documentReviewLoading) return

      this.documentRejectDialog = false
      this.documentReviewItem = null
      this.documentRejectReason = ''
      this.documentReviewError = ''
    },
    async submitDocumentReview(decision) {
      if (!this.documentReviewItem || this.documentReviewLoading) return

      const reason = String(this.documentRejectReason || '').trim()
      if (decision === 'REJECT' && !reason) {
        this.documentReviewError = 'Geçersiz sayma sebebi zorunludur.'

        return
      }
      if (reason.length > 500) {
        this.documentReviewError = 'Geçersiz sayma sebebi en fazla 500 karakter olabilir.'

        return
      }

      const reviewedDocument = this.documentReviewItem
      this.documentReviewLoading = true
      this.documentReviewAction = `${decision.toLowerCase()}-${reviewedDocument.id}`
      this.documentReviewError = ''
      this.documentLoadError = ''
      this.documentSuccessMessage = ''

      try {
        await axios.post(
          `${this.documentApiBase}/${reviewedDocument.id}/review`,
          {
            decision,
            rejectReason: decision === 'REJECT' ? reason : null,
          },
          this.requestOptions,
        )

        const successMessage = decision === 'REJECT'
          ? 'Belge geçersiz sayıldı; belgeye bağlı bekleyen para çekme talepleri reddedildi.'
          : 'Belge geçerli sayıldı. Para çekme talebi bekleyen durumda kaldı.'
        if (decision === 'REJECT') {
          const currentRequestId = this.normalizeDocumentId(this.editedItem.id)
          this.paymentStatus = 'FAILED'
          this.editedItem.status = 'FAILED'
          this.editedItem.failDescription = reason
          const row = this.desserts.find(item => Number(item.id) === currentRequestId)
          if (row) {
            row.status = 'FAILED'
            row.failDescription = reason
          }
          this.documentRejectDialog = false
        }

        this.documentReviewItem = null
        this.documentRejectReason = ''
        await this.loadWithdrawalDocuments(this.editedItem)
        if (decision === 'REJECT') {
          await this.initialize()
        }
        this.formError = ''
        this.documentSuccessMessage = successMessage
      } catch (error) {
        const message = this.resolveDocumentError(error, 'Belge inceleme kararı kaydedilemedi.')
        this.documentReviewError = message
        if (error?.response?.status === 409) {
          await this.loadWithdrawalDocuments(this.editedItem)
          this.documentLoadError = `${message} Belge durumu sunucudan yenilendi.`
          if (decision === 'REJECT') this.documentRejectDialog = false
        }
      } finally {
        this.documentReviewLoading = false
        this.documentReviewAction = ''
      }
    },
    resolveDocumentError(error, fallbackMessage) {
      const payload = error?.response?.data || {}
      const errorCode = payload.code || payload.errorCode || payload.message
      const messages = {
        DOCUMENT_VERSION_SUPERSEDED: 'Bu belge sürümünün yerine daha yeni bir sürüm yüklenmiş.',
        DOCUMENT_ALREADY_REVIEWED: 'Bu belge daha önce incelenmiş.',
        DIGITAL_BUSINESS_DOCUMENT_SNAPSHOT_INVALID: 'Para çekme talebinin belge sürümleri doğrulanamadı.',
        DOCUMENT_NOT_IN_WITHDRAWAL_SNAPSHOT: 'Belge bu para çekme talebine eklenen sürüm değil.',
        WITHDRAWAL_NOT_PENDING: 'Para çekme talebi artık bekleyen durumda değil.',
      }

      if (errorCode && messages[errorCode]) return messages[errorCode]
      if (error?.response?.status === 409) {
        return 'Belge başka bir işlemde değişti. Güncel durumu kontrol edip yeniden deneyin.'
      }

      return payload.message || fallbackMessage
    },
    closeInvoice() {
      this.invoiceDialog = false
    },
    openInvoice(item) {
      this.editedItem = item
      this.invoiceDialog = true
    },
    changeAccept() {},
    editItem(item) {
      this.closeDocumentPreview()
      this.documentRejectDialog = false
      this.documentReviewItem = null
      this.documentRejectReason = ''
      this.documentReviewError = ''
      this.editedIndex = item.id
      this.editedItem = {
        ...item,
        panelInvoice: this.normalizeBoolean(item.panelInvoice),
      }
      this.formError = ''
      this.originalInvoice = this.normalizeBoolean(item.panelInvoice)
      this.originalDescription = String(item.failDescription || '').trim()
      this.decisionConfirmed = false
      this.paymentStatus = item.status
      this.dialog = true
      this.loadWithdrawalDocuments(this.editedItem)
    },
    initialize() {
      if (this.loading) return Promise.resolve()
      this.loading = true
      this.loadError = ''

      return axios
        .get(`${process.env.VUE_APP_BASE_API_URL2}wallet/cms/paymentRequest`, this.requestOptions)
        .then(response => {
          if (!Array.isArray(response.data?.data)) throw new Error('Invalid payment response')
          this.desserts = response.data.data
        })
        .catch(error => {
          this.loadError = resolveApiErrorMessage(error, 'Ödeme talepleri yüklenemedi.')
        })
        .finally(() => { this.loading = false })
    },
    async save() {
      if (this.editedIndex < 0 || this.saving || this.documentReviewLoading) return
      if (!this.hasChanges || !this.decisionConfirmed) return
      if (!['PENDING', 'SUCCESS', 'FAILED'].includes(this.paymentStatus)) return

      const description = String(this.editedItem.failDescription || '').trim()
      if (this.paymentStatus === 'FAILED' && this.hasDigitalBusinessDocumentSnapshot) {
        if (this.documentLoading
          || this.documentLoadError
          || !this.hasCompleteDigitalBusinessDocumentSnapshot()) {
          this.formError = 'Belge durumu doğrulanmadan talep reddedilemez. Belgeyi tekrar yükleyin.'

          return
        }
        if (this.hasPendingDigitalBusinessDocuments) {
          this.formError = 'İnceleme bekleyen belge için genel reddetme kullanılamaz. “Geçersiz” aksiyonunu kullanın.'

          return
        }
      }
      if (this.paymentStatus === 'FAILED' && !description) {
        this.formError = 'Reddetme sebebi zorunludur.'

        return
      }
      if (description.length > 500) {
        this.formError = 'Reddetme sebebi en fazla 500 karakter olabilir.'

        return
      }
      if (this.paymentStatus === 'SUCCESS' && this.hasDigitalBusinessDocumentSnapshot) {
        if (this.documentLoading || this.documentLoadError) {
          this.formError = 'Belge durumu doğrulanmadan transfer tamamlanamaz. Belgeyi tekrar yükleyin.'

          return
        }
        const expectedIds = this.snapshotDocumentIds(this.editedItem)
        const documentsComplete = expectedIds.length === 1
          ? expectedIds.every(id => this.withdrawalDocuments.some(document => (
            document.id === id && document.status === 'APPROVED'
          )))
          : this.withdrawalDocuments.length === 1
            && this.withdrawalDocuments.every(document => document.status === 'APPROVED')
        if (!documentsComplete) {
          this.formError = 'Transferi tamamlamadan önce talebe eklenen 20/B istisna belgesini geçerli olarak işaretleyin.'

          return
        }
      }

      this.saving = true
      this.formError = ''
      try {
        await axios.put(
          `${process.env.VUE_APP_BASE_API_URL2}wallet/cms/paymentRequest`,
          {
            id: this.editedItem.id,
            invoicePanel: this.normalizeBoolean(this.editedItem.panelInvoice),
            paymentStatus: this.paymentStatus,
            description,
          },
          { headers: { Authorization: `Bearer ${user}` } },
        )
        this.successMessage = `#${this.editedItem.id} talebi kaydedildi.`
        this.saving = false
        this.close()
        await this.initialize()
      } catch (error) {
        this.formError = resolveApiErrorMessage(error, 'Para transfer isteği kaydedilemedi.')
      } finally {
        this.saving = false
      }
    },
    close() {
      if (this.saving) return
      this.dialog = false
      this.formError = ''
      this.documentRequestSerial += 1
      this.withdrawalDocuments = []
      this.documentLoading = false
      this.documentLoadError = ''
      this.documentSuccessMessage = ''
      this.closeDocumentPreview()
      this.documentRejectDialog = false
      this.documentReviewItem = null
      this.documentRejectReason = ''
      this.documentReviewError = ''
      setTimeout(() => {
        if (this.dialog) return
        this.editedItem = {
          id: '',
          name: '',
          logo: '',
          image: '',
          panelInvoice: false,
          categories: [{ id: '' }],
        }
        this.editedIndex = -1
      }, 300)
    },
  },
}
</script>

<style scoped>
.payment-filters { display: grid; grid-template-columns: minmax(200px, 2fr) repeat(3, minmax(140px, 1fr)) 40px; gap: 12px; align-items: center; }
.payment-review { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 28px; padding-top: 20px !important; }
.payment-context { min-width: 0; }
.payment-facts { display: grid; grid-template-columns: minmax(90px, 1fr) minmax(0, 2fr); gap: 10px; margin: 20px 0; }
.payment-facts dt { color: #555e6a; }
.payment-facts dd { margin: 0; overflow-wrap: anywhere; }
@media (max-width: 760px) { .payment-filters { grid-template-columns: 1fr 1fr; } .payment-review { grid-template-columns: 1fr; } }
.country-style {
  word-break: break-word;
}

.name-style {
  align-content: center;
  text-align: center;
}

.digital-business-documents {
  background: rgba(0, 0, 0, 0.015);
}

.digital-business-documents__item {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
}

.digital-business-documents__item-header,
.digital-business-documents__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.digital-business-documents__item-header {
  justify-content: space-between;
  align-items: flex-start;
}

.digital-business-documents__preview {
  min-height: 360px;
}

.digital-business-documents__preview-loading {
  min-height: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.digital-business-documents__preview-image {
  display: block;
  max-width: 100%;
  max-height: 72vh;
  margin: 0 auto;
  object-fit: contain;
}
</style>
